
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import ProfileCard from "@/components/ProfileCard";
import { SEARCH } from "@/constants/testIds";
import { toast } from "sonner";
import { SlidersHorizontal } from "lucide-react";

const DENOMS = ["", "Greek Orthodox", "Russian Orthodox", "Antiochian Orthodox", "Romanian Orthodox", "Bulgarian Orthodox", "Malankara Orthodox", "Coptic Orthodox"];

export default function Search() {
    const [sp, setSp] = useSearchParams();
    const [filters, setFilters] = useState({
        age_min: sp.get("age_min") || "",
        age_max: sp.get("age_max") || "",
        gender: sp.get("gender") || "",
        denomination: sp.get("denomination") || "",
        city: sp.get("city") || "",
        country: sp.get("country") || "",
        profession: sp.get("profession") || "",
        education: sp.get("education") || "",
        height_min: sp.get("height_min") || "",
    });
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchResults = async () => {
        setLoading(true);
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
        try {
            const { data } = await client.get(`/profiles/search?${params.toString()}`);
            setResults(data.results || []);
        } catch (e) { toast.error("Search failed"); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchResults(); }, [sp]);

    const apply = (e) => {
        e?.preventDefault?.();
        const next = new URLSearchParams();
        for (const [k, v] of Object.entries(filters)) if (v) next.set(k, v);
        setSp(next);
    };

    const shortlist = async (profile) => {
        try { await client.post(`/shortlist/${profile.user_id}`); toast.success("Shortlisted"); }
        catch { toast.error("Failed"); }
    };
    const interest = async (profile) => {
        try { await client.post(`/interests/${profile.user_id}`); toast.success("Interest sent"); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-12">
            <div className="text-[11px] tracking-[0.25em] uppercase text-gold-700 font-semibold">Browse</div>
            <h1 className="mt-2 font-serif text-5xl text-navy tracking-tighter">Discover faithful matches</h1>

            <div className="mt-10 grid lg:grid-cols-12 gap-8">
                {/* Filters */}
                <aside className="lg:col-span-3">
                    <form onSubmit={apply} className="bg-white border border-navy-100 rounded-2xl p-6 sticky top-24">
                        <div className="flex items-center gap-2 mb-5">
                            <SlidersHorizontal className="h-4 w-4 text-gold-600" />
                            <h2 className="font-serif text-2xl text-navy">Filters</h2>
                        </div>
                        <FInput label="Gender" testid={SEARCH.gender}>
                            <select value={filters.gender} onChange={(e) => setFilters({ ...filters, gender: e.target.value })} className="filter-input">
                                <option value="">Any</option><option value="male">Male</option><option value="female">Female</option>
                            </select>
                        </FInput>
                        <div className="grid grid-cols-2 gap-2">
                            <FInput label="Age min" testid={SEARCH.ageMin}>
                                <input type="number" value={filters.age_min} onChange={(e) => setFilters({ ...filters, age_min: e.target.value })} className="filter-input" />
                            </FInput>
                            <FInput label="Age max" testid={SEARCH.ageMax}>
                                <input type="number" value={filters.age_max} onChange={(e) => setFilters({ ...filters, age_max: e.target.value })} className="filter-input" />
                            </FInput>
                        </div>
                        <FInput label="Denomination" testid={SEARCH.denomination}>
                            <select value={filters.denomination} onChange={(e) => setFilters({ ...filters, denomination: e.target.value })} className="filter-input">
                                {DENOMS.map((d) => <option key={d} value={d}>{d || "Any"}</option>)}
                            </select>
                        </FInput>
                        <FInput label="City" testid={SEARCH.city}>
                            <input value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} className="filter-input" placeholder="Athens" />
                        </FInput>
                        <FInput label="Country">
                            <input value={filters.country} onChange={(e) => setFilters({ ...filters, country: e.target.value })} className="filter-input" placeholder="Greece" />
                        </FInput>
                        <FInput label="Profession" testid={SEARCH.profession}>
                            <input value={filters.profession} onChange={(e) => setFilters({ ...filters, profession: e.target.value })} className="filter-input" placeholder="Doctor" />
                        </FInput>
                        <FInput label="Min Height (cm)">
                            <input type="number" value={filters.height_min} onChange={(e) => setFilters({ ...filters, height_min: e.target.value })} className="filter-input" />
                        </FInput>
                        <button type="submit" data-testid={SEARCH.submit}
                            className="mt-4 w-full font-sans text-xs uppercase tracking-[0.18em] bg-navy text-white rounded-full py-3 hover:bg-navy-700 active:scale-95 transition-all">
                            Apply Filters
                        </button>
                    </form>
                </aside>

                {/* Results */}
                <div className="lg:col-span-9">
                    {loading ? (
                        <div className="text-center py-20 text-navy-400 font-sans">Searching…</div>
                    ) : results.length === 0 ? (
                        <div className="bg-linen rounded-2xl p-16 text-center">
                            <h3 className="font-serif text-3xl text-navy">No matches found</h3>
                            <p className="mt-2 text-navy-500 font-sans">Try adjusting your filters.</p>
                        </div>
                    ) : (
                        <>
                            <div className="text-sm font-sans text-navy-500 mb-6">{results.length} faithful match{results.length === 1 ? "" : "es"}</div>
                            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                                {results.map((p) => (
                                    <ProfileCard key={p.user_id} profile={p} onShortlist={shortlist} onInterest={interest} />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            <style>{`.filter-input { width: 100%; background: #FAFAFA; border: 1px solid #E2E8F0; border-radius: 10px; padding: 10px 14px; outline: none; font-size: 14px; }
                .filter-input:focus { box-shadow: 0 0 0 2px #D4AF37; border-color: transparent; }`}</style>
        </div>
    );
}

function FInput({ label, children, testid }) {
    return (
        <div className="mb-3">
            <label className="block text-[10px] tracking-[0.18em] uppercase text-navy-500 font-semibold mb-1.5">{label}</label>
            {React.Children.map(children, (c) => testid ? React.cloneElement(c, { "data-testid": testid }) : c)}
        </div>
    );
}
